export const chartRailHeight = 20;
export const chartRailGap = 8;
export const chartMinimumPlotHeight = 90;
export const splitLaneGap = 32;
export const flightChartSingleHeight = 500;
export const flightChartPadding = 54;
export const flightChartLayoutOptions = {
  bottomPadding: 14,
  rightPadding: 64,
};
export const explorerChartPadding = 70;
export const explorerChartLayoutOptions = {
  phaseRail: false,
  topPadding: 42,
  bottomPadding: 18,
  // The channel readout renders as a hover overlay inside the plot, so the
  // left/right padding only needs to clear the dual-axis tick labels (EGT
  // left, CHT right). 48px fits the widest label (~"1625") with a small gap
  // and lets the plot use more of the available width.
  leftPadding: 48,
  rightPadding: 48,
};

type ChartPaddingValue = number | ((canvas: any) => number);
interface ChartLayoutOptions {
  leftPadding?: ChartPaddingValue;
  topPadding?: ChartPaddingValue;
  rightPadding?: ChartPaddingValue;
  bottomPadding?: ChartPaddingValue;
  phaseRail?: boolean;
}

function chartOptionValue<T>(value: ChartPaddingValue | undefined, canvas: any, fallback: T): number | T {
  if (typeof value === "function") return value(canvas);
  return (value ?? fallback) as number | T;
}

export function chartBounds(canvas: any, padding = 48, options: ChartLayoutOptions = {}) {
  const leftPadding = chartOptionValue(options.leftPadding, canvas, padding);
  const topPadding = chartOptionValue(options.topPadding, canvas, padding);
  const rightPadding = chartOptionValue(options.rightPadding, canvas, padding * 0.5);
  const bottomPadding = chartOptionValue(options.bottomPadding, canvas, padding);
  return {
    left: leftPadding,
    top: topPadding,
    right: canvas.width - rightPadding,
    bottom: canvas.height - bottomPadding,
  };
}

export function chartRegions(canvas: any, padding = 48, options: ChartLayoutOptions = {}) {
  const outer = chartBounds(canvas, padding, options);
  if (options.phaseRail === false) {
    return {
      outer,
      plot: outer,
      phaseRail: null,
    };
  }
  const rail = {
    left: outer.left,
    right: outer.right,
    top: outer.bottom - chartRailHeight,
    bottom: outer.bottom,
  };
  const plotBottom = Math.max(outer.top + chartMinimumPlotHeight, rail.top - chartRailGap);
  return {
    outer,
    plot: {
      ...outer,
      bottom: Math.min(plotBottom, outer.bottom),
    },
    phaseRail: rail,
  };
}

export function plotHeightForCanvasHeight(height: number, padding: number, options: ChartLayoutOptions = {}) {
  const regions = chartRegions({ width: 1, height }, padding, options);
  return regions.plot.bottom - regions.plot.top;
}

export function canvasHeightForPlotHeight(plotHeight: number, padding: number, options: ChartLayoutOptions = {}) {
  const topPadding = (options.topPadding ?? padding) as number;
  const bottomPadding = (options.bottomPadding ?? padding) as number;
  return Math.ceil(plotHeight + topPadding + bottomPadding + chartRailHeight + chartRailGap);
}

export function syncFlightChartCanvasHeight(canvas, hasSplitLanes, padding, options: ChartLayoutOptions = {}) {
  const basePlotHeight = plotHeightForCanvasHeight(flightChartSingleHeight, padding, options);
  const plotHeight = hasSplitLanes
    ? basePlotHeight * 2 + splitLaneGap
    : basePlotHeight;
  const height = canvasHeightForPlotHeight(plotHeight, padding, options);
  if (canvas.height !== height) canvas.height = height;
  canvas.classList.toggle("is-split", height > flightChartSingleHeight);
}

export function syncCanvasDisplaySize(canvas) {
  const rect = canvas.getBoundingClientRect?.();
  const width = Math.round(rect?.width ?? 0);
  const height = Math.round(rect?.height ?? 0);
  if (width <= 0 || height <= 0) return false;
  if (canvas.width !== width) canvas.width = width;
  if (canvas.height !== height) canvas.height = height;
  return true;
}

export function plotLayout(canvas, padding, split, options: ChartLayoutOptions = {}) {
  const regions = chartRegions(canvas, padding, options);
  const full = regions.plot;
  if (!split.primary.length || !split.aux.length) {
    return {
      full,
      primary: split.primary.length ? full : null,
      aux: split.aux.length ? full : null,
      split,
      phaseRail: regions.phaseRail,
      outer: regions.outer,
    };
  }
  const gap = splitLaneGap;
  const height = full.bottom - full.top;
  const laneHeight = Math.round((height - gap) / 2);
  return {
    full,
    primary: { ...full, bottom: full.top + laneHeight },
    aux: { ...full, top: full.top + laneHeight + gap },
    split,
    phaseRail: regions.phaseRail,
    outer: regions.outer,
  };
}

export function interactionBounds(canvas, padding = 48, options: ChartLayoutOptions = {}) {
  const bounds = chartBounds(canvas, padding, options);
  const rect = canvas.getBoundingClientRect();
  const xScale = rect.width / Math.max(canvas.width, 1);
  const yScale = rect.height / Math.max(canvas.height, 1);
  return {
    left: bounds.left * xScale,
    right: bounds.right * xScale,
    top: bounds.top * yScale,
    bottom: bounds.bottom * yScale,
  };
}

export function chartInteractionCacheKey(canvas, padding, options: ChartLayoutOptions = {}, rect = canvas.getBoundingClientRect()) {
  return [
    canvas.width,
    canvas.height,
    Math.round(rect.left),
    Math.round(rect.top),
    Math.round(rect.width),
    Math.round(rect.height),
    padding,
    chartOptionValue(options.leftPadding, canvas, ""),
    chartOptionValue(options.topPadding, canvas, ""),
    chartOptionValue(options.bottomPadding, canvas, ""),
    chartOptionValue(options.rightPadding, canvas, ""),
  ].join("|");
}

export function cachedInteractionBounds(canvas, padding = 48, options: ChartLayoutOptions = {}) {
  const rect = canvas.getBoundingClientRect();
  const key = chartInteractionCacheKey(canvas, padding, options, rect);
  if (canvas._aeroInteractionBounds?.key === key) return canvas._aeroInteractionBounds.bounds;
  const bounds = interactionBounds(canvas, padding, options);
  canvas._aeroInteractionBounds = { key, bounds };
  return bounds;
}

export function chartPointerGeometry(canvas, padding = 48, options: ChartLayoutOptions = {}) {
  const rect = canvas.getBoundingClientRect();
  const key = chartInteractionCacheKey(canvas, padding, options, rect);
  if (canvas._aeroPointerGeometry?.key === key) return canvas._aeroPointerGeometry.geometry;
  const bounds = chartBounds(canvas, padding, options);
  const xScale = rect.width / Math.max(canvas.width, 1);
  const yScale = rect.height / Math.max(canvas.height, 1);
  const left = rect.left + bounds.left * xScale;
  const right = rect.left + bounds.right * xScale;
  const top = rect.top + bounds.top * yScale;
  const bottom = rect.top + bounds.bottom * yScale;
  const geometry = {
    left,
    right,
    top,
    bottom,
    width: Math.max(1, right - left),
    height: Math.max(1, bottom - top),
  };
  canvas._aeroPointerGeometry = { key, geometry };
  return geometry;
}
